import { useEffect, useState } from 'react';
import { useQuery, useMutation, type QueryOptions } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { DEFAULT_ENDPOINT_URL, useGqlClient } from '../client.ts';
import { getUsersEvaluationAdmin, getCars, getPayrollMonthOverview, getAllUsers, getAdminRequests, getCloseMonthHours, getUserTypes, hasSickReportsForMonth, getAdminDashboard, getAdminTrackings, sendPushNotificationMutation, sendReminderPushNotification, checkDatevData, getDatevDocumentsByUser, publishDatevData, isPublished, getDocumentCategories, getDocumentsForUser, createDocumentCategory, updateDocumentCategory, deleteDocumentCategory, getWorkDaysBetween, getDatevSettings, getDatevLogs, getDatevEmployeeValidationStatus, getValidToken, getCalenderMonth, getEmergencyServiceEnabled, getEmergencyServiceRate } from '../queries/admin_dashboard.ts';
import { deleteSickTracking, createOfftimeDay, updateOfftimeDay, deleteOfftimeDay, createCar, deleteCar, updateCar, updateUser, createUser, deleteUser, deleteTracking, getUserTrackings, closeMonthAdmin, updateTimetracking, fetchDataFromDatev, createDocument, updateDocument, deleteDocument, updateDatevSettings, sendNewUserMail, setEmergencyServiceRate, deletePendingEmergencyServiceRate, startDatevEmployeeValidation } from '../mutations/admin_dashboard.ts';
import type { GetUsersEvaluationAdminResponse, GetUsersEvaluationAdminVariables, GetCarsResponse, GetCarsVariables, GetPayrollOfficeOverviewResponse, GetPayrollOfficeOverviewVariables, GetAdminRequestsResponse, GetAdminRequestsVariables, GetCloseMonthHoursResponse, GetCloseMonthHoursVariables, CreateCarVariables, UpdateCarVariables, CloseMonthVariables, DeleteClosedMonthVariables, GetAdminDashboardResponse, GetAdminDashboardVariables, AdminTracking, UpdateTimetrackingVariables, DatevDocumentsByUserResponse, DatevDocumentsByUserVariables, DocumentCategory, DocumentsSorted, DatevSettings, UpdateDatevSettingsVariables, DatevLogsVariables, DatevLog, DatevEmployeeValidationStatus, DatevEmployeeValidationVariables, DatevSendStatus, CalenderMonthVariables, CalenderMonthDay, EmergencyServiceRate } from '../types/admin_dashboard.ts';
import type { CreateUserVariables, GetAllUsersResponse, GetAllUsersVariables, PaginatorInfo, ReopenMonthPayrollVariables, UpdateUserVariables } from '../types/user.ts';
import type { CreateOfftimeDayVariables, DeleteTrackingVariables, UpdateOfftimeDayVariables } from '../types/timeoff.ts';
import { deleteClosedMonth, reopenMonthPayroll } from '../mutations/user_dashboard.ts';
import { gql } from 'graphql-request'
import { useAuth } from './useAuth.ts';
import { uploadGraphQL } from './useUserDashboard.ts';

interface HasSickReportsResponse {
  hasSickReportsForMonth: boolean;
}

interface HasSickReportsVariables {
  month: number;
  year: number;
}

type UseAdminTrackingsQueryProps = {
  startDate: string
  endDate: string
  options?: QueryOptions<AdminTracking[]>
  enabled?: boolean
}

interface DatevExport {
  id: string;
  month: string;
  path: string;
  file_type: string;
}

interface HasExistingDatevDataResponse {
  existingDatevData: DatevExport[];
}

export const useUsersEvaluationAdmin = (
  variables: GetUsersEvaluationAdminVariables
) => {
  const gqlClient = useGqlClient();

  return useQuery<GetUsersEvaluationAdminResponse, Error>({
    queryKey: ['user-evaluation-admin', variables],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetUsersEvaluationAdminResponse,
        GetUsersEvaluationAdminVariables
      >(getUsersEvaluationAdmin, variables);

      return res;
    },
    enabled: Boolean(variables.month && variables.year),
  });
};

export const useCars = (first: number, page: number) => {
  const gqlClient = useGqlClient();

  return useQuery<GetCarsResponse, Error>({
    queryKey: ['cars', first, page],
    queryFn: async () => {
      return gqlClient.request<GetCarsResponse, GetCarsVariables>(
        getCars,
        { first, page }
      );
    },
  });
};

export const usePayrollOfficeOverview = (
  variables: GetPayrollOfficeOverviewVariables
) => {
  const gqlClient = useGqlClient();

  return useQuery<GetPayrollOfficeOverviewResponse, Error>({
    queryKey: ['payroll-office-overview', variables],
    queryFn: async () => {
      return gqlClient.request<
        GetPayrollOfficeOverviewResponse,
        GetPayrollOfficeOverviewVariables
      >(getPayrollMonthOverview, variables);
    },
    enabled: Boolean(variables.month && variables.year),
  });
};

export const useAllUsers = (
  page: number,
  limit: number,
  showInactive: boolean,
  search: string
) => {
  const gqlClient = useGqlClient();

  return useQuery<GetAllUsersResponse, Error>({
    queryKey: ['all-users', page, limit, showInactive, search],

    queryFn: async () => {
      return gqlClient.request<
        GetAllUsersResponse,
        GetAllUsersVariables
      >(getAllUsers, {
        page,
        limit,
        showInactive,
        search,
      });
    },

    staleTime: 0,
    enabled: Boolean(page && limit),
  });
};

export const useAdminRequests = (
  variables: GetAdminRequestsVariables
) => {
  const gqlClient = useGqlClient();

  return useQuery<GetAdminRequestsResponse, Error>({
    queryKey: ['admin-requests', variables],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetAdminRequestsResponse,
        GetAdminRequestsVariables
      >(getAdminRequests, variables);

      return res;
    },
  });
};

export const useCloseMonthHours = (
  variables: GetCloseMonthHoursVariables
) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['close-month-hours', variables.month, variables.year, variables.userId],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetCloseMonthHoursResponse,
        GetCloseMonthHoursVariables
      >(getCloseMonthHours, variables);

      return res.getCloseMonthHours;
    },
    enabled: !!variables.month && !!variables.year,
  });
};

export const useDeleteSickTracking = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { id: string }) => {
      const res = await gqlClient.request<
        { deleteSickTracking: boolean },
        { id: string }
      >(deleteSickTracking, variables);

      return res.deleteSickTracking;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sick-trackings'] });
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
    },
  });
};

export const useCreateOfftimeDay = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateOfftimeDayVariables) => {
      const res = await gqlClient.request<
        {
          createOfftimeDay: {
            id: string;
            date: string;
            name: string;
            length: number;
            is_holiday: boolean;
          };
        },
        CreateOfftimeDayVariables
      >(createOfftimeDay, variables);

      return res.createOfftimeDay;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offtime-days'] });
    },
  });
};

export const useUpdateOfftimeDay = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateOfftimeDayVariables) => {
      const res = await gqlClient.request<
        {
          updateOfftimeDay: {
            id: string;
            date: string;
            name: string;
            length: number;
            is_holiday: boolean;
          };
        },
        UpdateOfftimeDayVariables
      >(updateOfftimeDay, variables);

      return res.updateOfftimeDay;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offtime-days'] });
    },
  });
};

export const useDeleteOfftimeDay = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { id: string }) => {
      const res = await gqlClient.request<
        { deleteOfftimeDay: boolean },
        { id: string }
      >(deleteOfftimeDay, variables);

      return res.deleteOfftimeDay;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({  queryKey: ['offtime-days']  });
    },
  });
};

export const useCreateCar = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateCarVariables) => {
      const res = await gqlClient.request<
        { createCar: any },
        CreateCarVariables
      >(createCar, variables);

      return res.createCar;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cars'] });
    },
  });
};

export const useDeleteCar = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { id: string }) => {
      const res = await gqlClient.request<
        { deleteCar: boolean },
        { id: string }
      >(deleteCar, variables);

      return res.deleteCar;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({  queryKey: ['cars']  });
    },
  });
};

export const useUpdateCar = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateCarVariables) => {
      const res = await gqlClient.request<
        { updateCar: any },
        UpdateCarVariables
      >(updateCar, variables);

      return res.updateCar;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cars'] });
    },
  });
};

export const useUpdateUser = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateUserVariables) => {
      const res = await gqlClient.request<
        { updateUser: { id: string; full_name: string } },
        UpdateUserVariables
      >(updateUser, variables);

      return res.updateUser;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-users'] });
    },
  });
};

export const useUserTypes = () => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['user-types'],
    queryFn: async () => {
      const res = await gqlClient.request<{
        getUserTypes: { id: string; type: string }[];
      }>(getUserTypes);

      return res.getUserTypes;
    },
  });
};

export const useCreateUser = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateUserVariables) => {
      const res = await gqlClient.request<
        { createUser: any },
        CreateUserVariables
      >(createUser, variables);

      return res.createUser;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-users'] });
    },
  });
};

export const useDeleteUser = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { id: string }) => {
      const res = await gqlClient.request<
        { deleteUser: boolean },
        { id: string }
      >(deleteUser, variables);

      return res.deleteUser;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({  queryKey: ['all-users']  });
    },
  });
};

export const useDeleteTracking = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: DeleteTrackingVariables) => {
      const res = await gqlClient.request<
        { deleteTracking: boolean },
        DeleteTrackingVariables
      >(deleteTracking, variables);

      return res.deleteTracking;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-requests"] });
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
      queryClient.invalidateQueries({ queryKey: ['user-requests'] });
      queryClient.invalidateQueries({ queryKey: ['user-trackings-by-user'] });
    },
  });
};

export const useHasSickReports = (month: number, year: number) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['has-sick-reports', month, year],
    queryFn: async () => {
      const res = await gqlClient.request<
        HasSickReportsResponse,
        HasSickReportsVariables
      >(hasSickReportsForMonth, { month, year });

      return res.hasSickReportsForMonth;
    },
    enabled: Boolean(month && year),
  });
};


export const useUserTrackingsByUser = ({
  userId,
  month,
  year,
}: {
  userId?: string
  month?: number
  year?: number
}) => {
  const gqlClient = useGqlClient()

  return useQuery({
    queryKey: ['user-trackings-by-user', userId, month, year],
    enabled: !!userId,
    queryFn: async () => {
      const res = await gqlClient.request<{
        getUserTrackings: {
          id: string
          type: string
          client: string
          date: string
          duration: number
          comment: string
          locked: boolean
          isEmergency: boolean;
        }[]
      }>(getUserTrackings, {
        user_id: userId,
        month,
        year,
      })

      return res.getUserTrackings
    },
  })
};


export const useCloseMonthAdmin = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CloseMonthVariables) => {
      const res = await gqlClient.request<
        { closeMonthAdmin: { id: string } },
        CloseMonthVariables
      >(closeMonthAdmin, variables);

      return res.closeMonthAdmin;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payroll-overview"] });
      queryClient.invalidateQueries({ queryKey: ["user-evaluation-admin"] });
    },
  });
};

export const useDeleteClosedMonth = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: DeleteClosedMonthVariables) => {
      return gqlClient.request(deleteClosedMonth, variables);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user-evaluation-admin'],
      });

      queryClient.invalidateQueries({
        queryKey: ['close-month-hours'],
      });
    },
  });
};

export const useAdminDashboard = (
  startdate: string,
  enddate: string,
  userId?: string
) => {
  const gqlClient = useGqlClient();

  return useQuery<GetAdminDashboardResponse, Error>({
    queryKey: [
      'admin-dashboard',
      startdate,
      enddate,
      userId,
    ],

    queryFn: async () => {
      return gqlClient.request<
        GetAdminDashboardResponse,
        GetAdminDashboardVariables
      >(getAdminDashboard, {
        startdate,
        enddate,
        user_id: userId,
      });
    },

    enabled: Boolean(startdate && enddate),

    staleTime: 0,
  });
};

export const useAdminTrackingsQuery = (props: UseAdminTrackingsQueryProps) => {
  const gqlClient = useGqlClient()

  return useQuery({
    queryKey: ['admin-trackings', props.startDate, props.endDate],
    enabled: props.enabled ?? true,
    ...props.options,
    queryFn: async () => {
      const data = await gqlClient.request<{
        getAdminTrackings: AdminTracking[]
      }>(getAdminTrackings, {
        start_date: props.startDate,
        end_date: props.endDate,
      })

      return data.getAdminTrackings
    },
  })
};

export const useUpdateTimetracking = () => {
  const gqlClient = useGqlClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (variables: UpdateTimetrackingVariables) => {
      const data = await gqlClient.request(updateTimetracking, variables)
      return data.updateTimetracking
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['close-month-hours'] })
      queryClient.invalidateQueries({ queryKey: ['user-evaluation-admin'] })
    },
  })
}

export const useSendPushNotification = () => {
  const gqlClient = useGqlClient();

  return useMutation({
    mutationFn: async (variables: { user_id: string; title: string; body: string }) => {
      const data = await gqlClient.request(sendPushNotificationMutation, variables);
      return data.sendPushNotification;
    },
  });
};

export const useSendReminderPushNotification = () => {
  const gqlClient = useGqlClient();

  return useMutation({
    mutationFn: async (variables: { user_id: string; month: number; year: number }) => {
      const data = await gqlClient.request(sendReminderPushNotification, variables);
      return data.sendReminderPushNotification;
    },
  });
};

export const useHasExistingDatevData = (month: number, year: number) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['existing-datev-data', month, year],
    queryFn: async () => {
      const res = await gqlClient.request<
        HasExistingDatevDataResponse,
        HasSickReportsVariables
      >(checkDatevData, { month, year });

      return res.existingDatevData;
    },
    enabled: Boolean(month && year),
  });
};

export const useFetchDataFromDatev = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { month: number; year: number }) => {
      const data = await gqlClient.request(fetchDataFromDatev, variables);
      return data.fetchDataFromDatev;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['existing-datev-data', variables.month, variables.year],
      });
    },
    onError: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['existing-datev-data', variables.month, variables.year],
      });
    },
  });
};

export const useGetDatevDocumentsByUser = (
  first: number = 10,
  userId?: number,
  month?: number,
  year?: number,
) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['datev-documents-by-user', userId, month, year, first],
    queryFn: async () => {
      const res = await gqlClient.request<
        DatevDocumentsByUserResponse,
        DatevDocumentsByUserVariables
      >(getDatevDocumentsByUser, { first, userId, month, year });

      return res.datevPayrollDocuments;
    },
    enabled: Boolean(userId),
  });
};

export const usePublishDatevData = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { month: number; year: number }) => {
      const data = await gqlClient.request(publishDatevData, variables);
      return data.publishDatevData;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['isPublished', variables.month, variables.year],
      });
    },
  });
};

export const useIsPublished = (month: number, year: number) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['isPublished', month, year],
    queryFn: async () => {
      const data = await gqlClient.request<{ isPublished: boolean }>(
        isPublished,
        { month, year }
      );
      return data.isPublished;
    },
  });
};

export const sendDataToDatev = gql`
  mutation SendDataToDatev($month: Int!, $year: Int!) {
    sendDataToDatev(month: $month, year: $year)
  }
`;

const datevSendStatus = gql`
  query DatevSendStatus($month: Int!, $year: Int!) {
    datevSendStatus(month: $month, year: $year) {
      status
      error
      started_at
      finished_at
      total
      sent
      failed_personnel_numbers
    }
  }
`;

const datevSendStatusKey = (month: number, year: number) => ['datev-send-status', month, year];

// true now only means the send was started (or is already running), the result comes from useDatevSendStatus
export const useSendDataToDatev = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ month, year }: { month: number; year: number }) => {
      const data = await gqlClient.request<{ sendDataToDatev: boolean }>(
        sendDataToDatev,
        { month, year }
      );
      return data.sendDataToDatev;
    },
    onSuccess: (started, { month, year }) => {
      if (!started) return;
      queryClient.invalidateQueries({ queryKey: datevSendStatusKey(month, year) });
    },
  });
};

export const useDatevSendStatus = (month: number, year: number) => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  const query = useBackgroundJobStatus({
    queryKey: datevSendStatusKey(month, year),
    queryFn: async () => {
      const res = await gqlClient.request<{ datevSendStatus: DatevSendStatus }>(
        datevSendStatus,
        { month, year }
      );
      return res.datevSendStatus;
    },
    pollInterval: 5000,
    maxWaitMs: 35 * 60 * 1000,
  });

  const status = query.data?.status;

  // once the send finishes, the "sendable" state of the month changes
  useEffect(() => {
    if (status === 'completed' || status === 'failed') {
      queryClient.invalidateQueries({ queryKey: ['datevDataSendable', month, year] });
    }
  }, [status, month, year, queryClient]);

  return query;
};

const datevDataSendable = gql`
  query DatevDataSendable($month: Int!, $year: Int!) {
    datevDataSendable(month: $month, year: $year)
  }
`;

export const useDatevDataSendable = (month: number, year: number) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['datevDataSendable', month, year],
    queryFn: async () => {
      const data = await gqlClient.request<{ datevDataSendable: string[] }>(
        datevDataSendable,
        { month, year }
      );
      return data.datevDataSendable;
    },
  });
};

export const useDocumentCategories = ()  => {
  const gqlClient = useGqlClient();
  return useQuery({
    queryKey: ['documentCategories'],
    queryFn: async () => {
      const data = await gqlClient.request<{ getDocumentCategories: DocumentCategory[] }>(
        getDocumentCategories
      );
      return data.getDocumentCategories;
    },
  });
}
 
export const useDocumentsForUser = (user_id: string) => {
  const gqlClient = useGqlClient();
  return useQuery({
    queryKey: ['documentsForUser', user_id],
    queryFn: async () => {
      const data = await gqlClient.request<{ getDocumentsForUser: DocumentsSorted[] }>(
        getDocumentsForUser,
        { user_id }
      );
      return data.getDocumentsForUser;
    },
    enabled: !!user_id,
  });
}
 
export const useCreateDocument = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      user_id: string;
      document_category_id: string;
      file: File | null;
      valid_from: string | null;
      valid_until: string | null;
    }) => {
      const res = await uploadGraphQL(
        DEFAULT_ENDPOINT_URL,
        createDocument,
        variables,
        token
      );

      if (res?.errors?.length) {
        throw new Error(res.errors[0]?.message || 'Serverfehler');
      }

      if (!res?.data?.createDocument) {
        throw new Error('Serverfehler');
      }

      return res.data.createDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentsForUser'] });
    },
  });
};

export const useUpdateDocument = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: {
      id: string;
      file: File | null;
      valid_from: string | null;
      valid_until: string | null;
    }) => {
      const res = await uploadGraphQL(
        DEFAULT_ENDPOINT_URL,
        updateDocument,
        variables,
        token
      );

      if (res?.errors?.length) {
        throw new Error(res.errors[0]?.message || 'Serverfehler');
      }

      if (!res?.data?.updateDocument) {
        throw new Error('Serverfehler');
      }

      return res.data.updateDocument;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentsForUser'] });
    },
  });
};
 
export function useDeleteDocument() {
  const gqlClient = useGqlClient();
  return useMutation({
    mutationFn: (id: string) =>
      gqlClient.request(deleteDocument, { id }),
  });
}

export function useSendNewUserEmail() {
  const gqlClient = useGqlClient();
  return useMutation({
    mutationFn: (user_id: string) =>
      gqlClient.request(sendNewUserMail, { user_id }),
  });
}

export function useCreateDocumentCategory() {
  const gqlClient = useGqlClient();
  return useMutation({
    mutationFn: (variables: {
      name: string;
      validity_months: number | null;
      has_expiry: boolean;
    }) => gqlClient.request<{ createDocumentCategory: DocumentCategory }>(
      createDocumentCategory,
      variables
    ),
  });
}
 
export function useUpdateDocumentCategory() {
  const gqlClient = useGqlClient();
  return useMutation({
    mutationFn: (variables: {
      id: string;
      name?: string;
      validity_months?: number | null;
      has_expiry?: boolean;
    }) => gqlClient.request<{ updateDocumentCategory: DocumentCategory }>(
      updateDocumentCategory,
      variables
    ),
  });
}
 
export function useDeleteDocumentCategory() {
  const gqlClient = useGqlClient();
  return useMutation({
    mutationFn: (id: string) =>
      gqlClient.request<{ deleteDocumentCategory: boolean }>(
        deleteDocumentCategory,
        { id }
      ),
  });
}

export function useWorkDaysBetween(userID: string, start: string, end: string) {
  const gqlClient = useGqlClient();
  return useQuery({
    queryKey: ['workDaysBetween', userID, start, end],
    queryFn: async () => {
      const data = await gqlClient.request<{ getWorkDaysBetween: number }>(
        getWorkDaysBetween,
        { userID, start, end }
      );
      return data.getWorkDaysBetween;
    },
    enabled: !!userID && !!start && !!end,
  });
}

export const useGetDatevSettings = () => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['datev-settings'],
    queryFn: async () => {
      const res = await gqlClient.request<{ datevSettings: DatevSettings }>(getDatevSettings);
      return res.datevSettings;
    },
  });
}

export const useUpdateDatevSettings = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: UpdateDatevSettingsVariables) => {
      const res = await gqlClient.request<
        { updateDatevSettings: DatevSettings },
        UpdateDatevSettingsVariables
      >(updateDatevSettings, variables);
      return res.updateDatevSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['datev-settings'] });
    },
  });
}

export const useGetDatevLogs = (variables: DatevLogsVariables) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['datev-logs', variables],
    queryFn: async () => {
      const res = await gqlClient.request<{
        datevLogs: {
          data: DatevLog[];
          paginatorInfo: PaginatorInfo;
        };
      }>(getDatevLogs, variables);
      return res.datevLogs;
    },
  });
};

type BackgroundJobStatus = {
  status: 'idle' | 'running' | 'completed' | 'failed';
  started_at: string | null;
};

// Polls the status of a backend job while it is running. Stops on completed / failed / idle,
// or after maxWaitMs as a safety net in case the job hangs (isTimedOut).
const useBackgroundJobStatus = <T extends BackgroundJobStatus>({
  queryKey,
  queryFn,
  pollInterval,
  maxWaitMs,
}: {
  queryKey: unknown[];
  queryFn: () => Promise<T>;
  pollInterval: number;
  maxWaitMs: number;
}) => {
  const [timedOutRunId, setTimedOutRunId] = useState<string | null>(null);
  const [resumeCount, setResumeCount] = useState(0);

  // identifies a running job, so a new job (or another month/year) gets a fresh wait window
  const getRunId = (data?: T) =>
    data?.status === 'running' ? `${JSON.stringify(queryKey)}-${data.started_at}` : null;

  const query = useQuery({
    queryKey,
    queryFn,
    refetchInterval: (q) => {
      const runId = getRunId(q.state.data);
      return runId && runId !== timedOutRunId ? pollInterval : false;
    },
  });

  const runId = getRunId(query.data);
  const isTimedOut = runId !== null && runId === timedOutRunId;

  useEffect(() => {
    if (!runId) return;
    const t = setTimeout(() => setTimedOutRunId(runId), maxWaitMs);
    return () => clearTimeout(t);
  }, [runId, maxWaitMs, resumeCount]);

  const resumePolling = () => {
    setTimedOutRunId(null);
    setResumeCount((c) => c + 1);
  };

  return { ...query, isTimedOut, resumePolling };
};

const datevValidationStatusKey = (variables: DatevEmployeeValidationVariables) => [
  'datev-employee-validation-status',
  variables,
];

export const useDatevEmployeeValidationStatus = (variables: DatevEmployeeValidationVariables) => {
  const gqlClient = useGqlClient();

  return useBackgroundJobStatus({
    queryKey: datevValidationStatusKey(variables),
    queryFn: async () => {
      const res = await gqlClient.request<
        { datevEmployeeValidationStatus: DatevEmployeeValidationStatus },
        DatevEmployeeValidationVariables
      >(getDatevEmployeeValidationStatus, variables);
      return res.datevEmployeeValidationStatus;
    },
    pollInterval: 3000,
    maxWaitMs: 5 * 60 * 1000,
  });
};

export const useStartDatevEmployeeValidation = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: DatevEmployeeValidationVariables) => {
      const res = await gqlClient.request<
        { startDatevEmployeeValidation: Pick<DatevEmployeeValidationStatus, 'status' | 'error' | 'started_at'> },
        DatevEmployeeValidationVariables
      >(startDatevEmployeeValidation, variables);
      return res.startDatevEmployeeValidation;
    },
    onSuccess: (data, variables) => {
      // write the new status into the cache so polling kicks in right away
      queryClient.setQueryData<DatevEmployeeValidationStatus>(
        datevValidationStatusKey(variables),
        (prev) => ({
          finished_at: null,
          ...prev,
          ...data,
          results: data.status === 'running' ? [] : prev?.results ?? [],
        }),
      );
      if (data.status !== 'running') {
        queryClient.invalidateQueries({ queryKey: datevValidationStatusKey(variables) });
      }
    },
  });
};

export const useGetValidToken = () => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['datev-valid-token'],
    queryFn: async () => {
      const res = await gqlClient.request<{ getvalidToken: boolean }>(getValidToken);
      return res.getvalidToken;
    },
  });
};

export const useReopenMonthPayroll = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: ReopenMonthPayrollVariables) => {
      return gqlClient.request(reopenMonthPayroll, variables);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user-evaluation-admin'],
      });

      queryClient.invalidateQueries({
        queryKey: ['close-month-hours'],
      });
    },
  });
};

export const useGetCalenderMonth = (variables: CalenderMonthVariables) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['calender-month', variables],
    queryFn: async () => {
      const res = await gqlClient.request<{
        getCalenderMonth: CalenderMonthDay[];
      }>(getCalenderMonth, variables);
      return res.getCalenderMonth;
    },
  });
};

export const useUserHasEmergencyService = (userId?: string | null) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['getEmergencyServiceEnabled', userId],
    queryFn: async () => {
      const res = await gqlClient.request(getEmergencyServiceEnabled, {
        userId,
      });
      return res.getEmergencyServiceEnabled;
    },
    enabled: !!userId,
  });
};

export const useEmergencyServiceRates = () => {
  const gqlClient = useGqlClient();
 
  return useQuery({
    queryKey: ['getEmergencyServiceRate'],
    queryFn: async () => {
      const res = await gqlClient.request(getEmergencyServiceRate);
      return res.getEmergencyServiceRates as EmergencyServiceRate[];
    },
  });
};
 
export const useSetEmergencyServiceRate = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();
 
  return useMutation({
    mutationFn: async (input: {
      effective_date: string;
      weekday_factor: number;
      special_day_factor: number;
    }) => {
      const res = await gqlClient.request(setEmergencyServiceRate, input);
      return res.setEmergencyServiceRate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getEmergencyServiceRate'] });
    },
  });
};
 
export const useDeletePendingEmergencyServiceRate = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();
 
  return useMutation({
    mutationFn: async () => {
      const res = await gqlClient.request(deletePendingEmergencyServiceRate);
      return res.deletePendingEmergencyServiceRate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getEmergencyServiceRate'] });
    },
  });
};
